import { EmailValidator, useForm } from '@chiroup/hooks';
import { useEffect, useMemo, useState } from 'react';
import userService from '../../services/user.service';
import Logo from '../layout/Logo';
import SeminarForm from './SeminarForm';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import {
  SeminarMeData,
  SeminarSignupForm,
} from '@chiroup/core/types/Seminar.type';

const getJWTData = async (newToken: string, valid: boolean) => {
  if (!newToken || !valid) return;
  return userService.getJWTData(newToken);
};

const defaultValues = {
  email: '',
  fname: '',
  lname: '',
  name: '',
  city: '',
  country: '',
  address1: '',
  state: '',
  zip: '',
  clinicID: undefined,
  promo: '',
};

export enum FormStates {
  CheckEmailValidity,
  ValidationCode,
  UserForm,
  NotAUserForm,
}

export enum EmailStatus {
  NotChecked,
  Valid,
  Invalid,
}

const Seminar = () => {
  const [emailStatus, setEmailStatus] = useState<number>(
    EmailStatus.NotChecked,
  );
  const [userData, setUserData] = useState<{
    seminarMe: SeminarMeData;
    token: string;
  }>();
  const [lastCheckedEmail, setLastCheckedEmail] = useState('');
  const [formState, setFormState] = useState<FormStates>(
    FormStates.CheckEmailValidity,
  );
  const [token, setToken] = useState('');
  const [validationAttempts, setValidationAttempts] = useState(0);

  const validation = useMemo(() => {
    switch (formState) {
      case FormStates.CheckEmailValidity:
        return {
          email: {
            pattern: EmailValidator,
            required: {
              message: 'Email name is required.',
            },
          },
        };
      case FormStates.ValidationCode:
        return {
          validationCode: {
            pattern: {
              value: /^\d{6}$/,
              message: 'Please enter a valid email address.',
            },
          },
        };
      default: {
        return {
          email: {
            pattern: EmailValidator,
            required: {
              message: 'Email is required.',
            },
          },
          fname: {
            required: {
              message: 'First name is required.',
            },
          },
          lname: {
            required: {
              message: 'Last name is required.',
            },
          },
          name: {
            required: {
              message: 'Clinic name is required.',
            },
          },
          country: {
            required: {
              message: 'Country is required.',
            },
          },
          address1: {
            required: {
              message: 'Street address is required.',
            },
          },
          city: {
            required: {
              message: 'City is required.',
            },
          },
          state: {
            required: {
              message: 'State is required.',
            },
          },
          zip: {
            required: {
              message: 'Zip code is required.',
            },
          },
        };
      }
    }
  }, [formState]);

  const { value, registerSubmit, onChange, errors, isSubmitting } =
    useForm<SeminarSignupForm>(defaultValues, validation);
  const location = useLocation();

  const navigateToWebsite = (url: string) => {
    if (!url) {
      console.error('URL is not provided.');
      return;
    }

    window.location.href = url;
  };

  useEffect(() => {
    if (value.email !== lastCheckedEmail && validationAttempts > 0) {
      setValidationAttempts(0);
    }
  }, [value.email, lastCheckedEmail, validationAttempts]);

  const onSubmit = async ({
    email,
    validationCode,
  }: Partial<SeminarSignupForm>): Promise<Partial<SeminarSignupForm>> => {
    let url;
    const queryParams = qs.parse(location.search);
    const promo = queryParams.promo as string;

    switch (formState) {
      case FormStates.CheckEmailValidity:
        if (!email) return value;
        if (
          (email && lastCheckedEmail !== email) ||
          (email && lastCheckedEmail === email && validationAttempts > 0)
        ) {
          setEmailStatus(EmailStatus.NotChecked);

          const zbResult = await userService.checkIfValidEmail(email);

          let newValidationAttempts = validationAttempts;
          let newEmailStatus = emailStatus;
          setLastCheckedEmail(email);
          setValidationAttempts((prev) => prev + 1);

          const emailStatusRes = zbResult?.data?.status?.toLowerCase();
          switch (emailStatusRes) {
            case 'valid':
              newEmailStatus = EmailStatus.Valid;
              setEmailStatus(EmailStatus.Valid);
              break;
            case 'invalid':
              newEmailStatus = EmailStatus.Invalid;
              setEmailStatus(EmailStatus.Invalid);
              break;
            default:
              newEmailStatus = EmailStatus.Invalid;
              setEmailStatus(EmailStatus.Invalid);
              break;
          }

          if (
            newEmailStatus === EmailStatus.Valid ||
            newValidationAttempts === 1
          ) {
            newValidationAttempts += 1;
            const res = await userService.checkExistsAndValidate(email);
            if (res?.exists) {
              setFormState(FormStates.ValidationCode);
            } else {
              if (res.token) {
                setToken(res.token);
              }
              setFormState(FormStates.NotAUserForm);
            }
          } else {
            setEmailStatus(EmailStatus.Invalid);
            newValidationAttempts += 1;
          }
        }

        return value;

      case FormStates.ValidationCode:
        if (!validationCode) return value;

        // eslint-disable-next-line no-case-declarations
        const res = await userService.checkValidationCode(
          email || '',
          validationCode,
        );
        if (res?.token) {
          const jwtData = await getJWTData(res.token, res.valid);
          setUserData(jwtData);
          onChange('fname')(jwtData?.seminarMe?.user?.fname);
          onChange('lname')(jwtData?.seminarMe?.user?.lname);
          setFormState(FormStates.UserForm);
        }

        return value;

      case FormStates.UserForm:
        url = await userService.createSeminarUserClinic(
          userData?.token ?? token,
          { ...value, promo },
        );
        if (url) {
          navigateToWebsite(url);
        }

        return value;

      case FormStates.NotAUserForm:
        url = await userService.createSeminarUserClinic(
          userData?.token ?? token,
          { ...value, promo },
        );
        if (url) {
          navigateToWebsite(url);
        }

        return value;

      default:
        return value;
    }
  };

  const sendNewCode = async () => {
    await userService.checkExistsAndValidate(value.email || '');
  };

  return (
    <div>
      <div className="sm:hidden">
        <Logo className="mt-8 ml-4" />
      </div>
      <div
        className="grid h-screen bg-left-top bg-cover place-items-center"
        style={{ backgroundImage: 'none' }}
      >
        <div className="flex justify-center w-full h-screen max-w-3xl bg-white sm:box-border sm:border-12 sm:border-gray-300 sm:rounded-3xl sm:w-2/3 sm:h-fit dark:bg-gray-800">
          <div className="flex flex-row flex-auto">
            <div className="pt-5 pl-5 pr-5 sm:w-2/3 sm:p-10">
              <div className="leading-4 sm:text-center">
                <p className="w-screen mb-4 text-2xl font-medium leading-6 text-left text-primary-500 lg:text-3xl sm:text-xl sm:w-auto">
                  {formState === FormStates.CheckEmailValidity
                    ? 'Welcome to ChiroUp.'
                    : "You're almost in!"}
                </p>
                <p className="mb-6 text-sm text-left lg:text-lg">
                  {formState === FormStates.CheckEmailValidity
                    ? 'Sign up now!'
                    : 'Just a few more details to create your account.'}
                </p>
              </div>
              <form onSubmit={registerSubmit(onSubmit, {})}>
                <SeminarForm
                  value={value}
                  onChange={onChange}
                  errors={errors}
                  formState={formState}
                  isSubmitting={isSubmitting}
                  emailStatus={emailStatus}
                  sendNewCode={sendNewCode}
                  token={userData?.token ?? token}
                  userData={userData?.seminarMe}
                  setUserData={setUserData}
                  onSubmit={onSubmit}
                />
              </form>
            </div>
            <div
              className="box-border flex-col hidden w-1/3 space-y-12 bg-gray-300 border-primary-100 rounded-l-md lg:space-y-16 sm:block"
              style={{ marginLeft: '1px' }}
            >
              <div className="center">
                <Logo className="mt-5 ml-auto mr-auto logo-light" />
              </div>
              <div className="w-100">
                <p className="ml-5 mr-5 italic text-center text-gray-700 md:leading-7 xs:text-md md:text-lg lg:text-xl">
                  "The greatest addition for patient education, retention, and
                  compliance."
                </p>
              </div>
              <p className="ml-5 mr-5 text-center text-gray-700 text-md leading-1 lg:leading-5">
                Trusted by over 2,500 chiropractors around the world.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Seminar;
